import { createPopper, Instance } from '@popperjs/core';
import { Component } from 'react';

import { IconHelper } from '../../../utils/icon-helper';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import Tooltip from '../tooltip/tooltip.component';
import style from './dropdown.shared.style.module.scss';

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * If a dropdown value object carries a 6-digit hex color in its `value` property
 * (e.g. convert-options DROPDOWN_OPTION color nodes), return that hex so a swatch
 * can be rendered next to the label. Any other value shape renders unchanged.
 */
function getSwatchColor(value: unknown): string | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const hex = (value as { value?: unknown }).value;
    return typeof hex === 'string' && HEX_COLOR_RE.test(hex) ? hex : null;
}


export interface DropdownProps<T> {
    children?: any;
    values: T[];
    valueFormatter: (value: T) => string;
    hasTooltip: (value: T) => boolean;
    getTooltip: (value: T) => string;
    selectedValueFormatter?: (value: T) => string;
    initialValue?: T | undefined;
    placeholder?: string;
    label?: string;
    btnClassNames?: string;
    btnGroupClassNames?: string;
    firstBtnStyles?: any;
    onSelect?: (selectedValue: T, forced?: boolean) => void
    canSelect?: (selectedValue: T) => Promise<boolean>
    shownFn?: (value: T) => boolean;
    emitInitialValueOnInit?: boolean;
    searchable?: boolean;
}

interface DropdownState<T> {
    values: T[];
    selectedValue?: T | undefined;
    shown?: boolean;
    prevInitialValue?: T | undefined;
    searchTerm: string;
    isPopperReady: boolean;
}

export default class Dropdown<T> extends Component<DropdownProps<T>, DropdownState<T>> {
    wrapperRef: any;
    btnClassNames = 'x-btn x-btn-gray x-btn-gray-outline';
    firstBtnStyles = {};

    // Popper refs
    referenceElement: HTMLElement | null = null;
    popperElement: HTMLElement | null = null;
    popperInstance: Instance | null = null;

    readonly state: Readonly<DropdownState<T>> = {
        selectedValue: this.props.initialValue,
        values: this.props.values,
        shown: false,
        prevInitialValue: this.props.initialValue, // track initial value
        searchTerm: '',
        isPopperReady: false
    };
    constructor(props: DropdownProps<T>) {
        super(props);
        this.firstBtnStyles = this.props.firstBtnStyles;
        this.handleClickOutside = this.handleClickOutside.bind(this);

        this.btnClassNames = this.props.btnClassNames ?? this.btnClassNames
    }

    componentDidMount() {
        if (this.props.emitInitialValueOnInit) {
            this.updateSelectedValue(this.props.initialValue);
        }
    }

    componentWillUnmount() {
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    }

    setWrapperRef(node: any) {
        this.wrapperRef = node;
    }

    setReferenceRef(node: any) {
        this.referenceElement = node;
    }

    setPopperRef(node: any) {
        this.popperElement = node;
    }

    handleClickOutside(event: any) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.toggleDropdown();
        }
    }

    toggleDropdown() {
        if (!this.state.shown) {
            document.addEventListener('mousedown', this.handleClickOutside, { passive: true });
        } else {
            document.removeEventListener('mousedown', this.handleClickOutside);
        }

        const nextShown = !this.state.shown;

        this.setState({
            shown: nextShown,
            searchTerm: '', // Reset search when toggled
            isPopperReady: false // Reset popper ready state
        }, () => {
            if (nextShown) {
                this.createPopperInstance();
            } else {
                this.destroyPopperInstance();
            }
        });
    }

    createPopperInstance() {
        if (this.referenceElement && this.popperElement) {
            this.popperInstance = createPopper(this.referenceElement, this.popperElement, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 4],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: 8,
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            padding: 8,
                        },
                    },
                    {
                        name: 'computeStyles',
                        options: {
                            gpuAcceleration: false, // Use top/left instead of transform to avoid conflict with CSS animation
                        },
                    }
                ],
                onFirstUpdate: () => {
                    this.setState({ isPopperReady: true });
                }
            });
        }
    }

    destroyPopperInstance() {
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    }

    handleSearchChange(e: any) {
        this.setState({
            searchTerm: e.target.value
        });
    }

    clearSearch() {
        this.setState({
            searchTerm: ''
        });
    }

    async userTapedOnDropdownItem(selectedValue: T) {
        if (this.props.canSelect) {
            if (!await this.props.canSelect(selectedValue)) {
                return;
            }
        }
        this.updateSelectedValue(selectedValue);
        this.toggleDropdown();
    }

    updateSelectedValue(selectedValue: T, forced = false) {
        this.setState({
            selectedValue: selectedValue,
        });
        if (this.props.onSelect) {
            this.props.onSelect(selectedValue, forced);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.valueFilter(this.state.selectedValue)) {
            this.updateSelectedValue(this.props.values.find((v) => this.valueFilter(v)), true);
        }

        if (this.state.shown && !prevState.shown) {
            // Scroll selected item into view
            setTimeout(() => {
                const activeEl = this.wrapperRef?.querySelector(`.${style.active}`);
                const containerEl = this.wrapperRef?.querySelector(`.${style['dropdown-content']}`);
                if (activeEl && containerEl) {
                    containerEl.scrollTop = activeEl.offsetTop - (containerEl.clientHeight / 2) + (activeEl.clientHeight / 2);
                }
            }, 50);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const newState: any = {
            values: props.values
        };

        // Detect change in initialValue prop
        if (props.initialValue !== state.prevInitialValue) {
            newState.selectedValue = props.initialValue;
            newState.prevInitialValue = props.initialValue;
        }
        // If initialValue didn't change, check if current selection is still valid
        else if (!props.values.some(item => item === state.selectedValue)) {
            // Current selection is invalid (not in new values list), revert to initialValue or clear
            newState.selectedValue = props.initialValue;
        }

        return newState;
    }

    private valueFilter(value: T) {
        return !this.props.shownFn || this.props.shownFn(value);
    }

    private renderColorSwatch(value: T) {
        const color = getSwatchColor(value);
        if (!color) {
            return null;
        }
        return <span
            className={style['color-swatch']}
            style={{ backgroundColor: color }}
            aria-hidden="true"
        ></span>;
    }

    render() {
        const getSelectedValue = () => {
            if (this.props.selectedValueFormatter) {
                return this.props.selectedValueFormatter(this.state.selectedValue)
            }
            if (this.props.valueFormatter) {
                return this.props.valueFormatter(this.state.selectedValue)
            }
            return this.props.placeholder || 'Not Selected'
        }
        return (
            <div ref={(node) => this.setWrapperRef(node)}
                className={`${style.dropdown} ${this.props.btnGroupClassNames || ''}`}
            >
                {
                    <div key="dropdownTrigger" ref={(node) => this.setReferenceRef(node)} className={`x-btn-group ${style['dropdown-btn-group']}`} onClick={() => this.toggleDropdown()}>
                        {
                            this.props.label && <button type="button" className={`${this.btnClassNames} ${style['label']}`} disabled>{this.props.label}
                            </button>
                        }
                        <button type="button" className={`${this.btnClassNames} ${style['value']}`} style={this.firstBtnStyles}>
                            {this.renderColorSwatch(this.state.selectedValue)}
                            {getSelectedValue()}
                        </button>
                        <button type="button" className={`${this.btnClassNames} ${style['caret']}`}>&#9660;</button>
                    </div>
                }
                <ul key="dropdownMenu" ref={(node) => this.setPopperRef(node)} className={`${style["dropdown-content"]} ${this.state.shown ? style.show : ''}`} style={{ visibility: this.state.isPopperReady ? 'visible' : 'hidden' }}>
                    {
                        this.state.shown && this.props.searchable && (
                            <li className={style['search-item']} onClick={(e) => e.stopPropagation()}>
                                <div className={style['search-wrapper']}>
                                    <input
                                        type="text"
                                        className={style['dropdown-search']}
                                        placeholder="Search..."
                                        value={this.state.searchTerm}
                                        onInput={(e) => this.handleSearchChange(e)}
                                        autoFocus
                                    />
                                    {this.state.searchTerm && (
                                        <button
                                            className={style['search-clear-btn']}
                                            onClick={() => this.clearSearch()}
                                            title="Clear search"
                                        >
                                            &times;
                                        </button>
                                    )}
                                </div>
                            </li>
                        )
                    }
                    {
                        this.state.shown && this.state.values
                            .filter(val => this.valueFilter(val))
                            .filter(val => {
                                if (!this.state.searchTerm) return true;
                                const label = this.props.valueFormatter(val).toLowerCase();
                                return label.includes(this.state.searchTerm.toLowerCase());
                            })
                            .map((val, index) => {
                                return (
                                    <li key={`${index}`} className={`${this.props.valueFormatter(val) === this.props.valueFormatter(this.state.selectedValue) ? style.active : ''}`}
                                        onClick={() => this.userTapedOnDropdownItem(val)}>{this.renderColorSwatch(val)}{this.props.valueFormatter(val)}
                                        <span className={style.tooltip}>
                                            {
                                                this.props.hasTooltip(val) &&
                                                <Tooltip text={this.props.getTooltip(val)}>
                                                    {
                                                        IconHelper.getIconElement(HtmlEntityTypes.INFO_WITH_CIRCLE)
                                                    }
                                                </Tooltip>
                                            }
                                        </span>
                                    </li>
                                )
                            })
                    }
                </ul>
            </div>
        );
    }
}